"use client"

import { useState, useEffect } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import LabourBurdenCalculator from "@/components/labour-burden-calculator"
import { useAuth } from "@/contexts/auth-context"
import { useCodeValues } from "@/hooks/use-code-values"

// Subdivision data mapping - mirrors the structure from Angular's CodeValue system
// This should ideally come from an API call, but for now we'll maintain it statically
const SUBDIVISIONS = [
  { id: 592, code: "CA-AB", text: "Alberta", provinceCode: "AB" },
  { id: 593, code: "CA-BC", text: "British Columbia", provinceCode: "BC" },
  { id: 594, code: "CA-MB", text: "Manitoba", provinceCode: "MB" },
  { id: 595, code: "CA-NB", text: "New Brunswick", provinceCode: "NB" },
  { id: 596, code: "CA-NL", text: "Newfoundland and Labrador", provinceCode: "NL" },
  { id: 597, code: "CA-NS", text: "Nova Scotia", provinceCode: "NS" },
  { id: 598, code: "CA-NT", text: "Northwest Territories", provinceCode: "NT" },
  { id: 599, code: "CA-NU", text: "Nunavut", provinceCode: "NU" },
  { id: 600, code: "CA-ON", text: "Ontario", provinceCode: "ON" },
  { id: 601, code: "CA-PE", text: "Prince Edward Island", provinceCode: "PE" },
  { id: 602, code: "CA-QC", text: "Quebec", provinceCode: "QC" },
  { id: 603, code: "CA-SK", text: "Saskatchewan", provinceCode: "SK" },
  { id: 604, code: "CA-YT", text: "Yukon", provinceCode: "YT" },
]

// Utility function to get subdivision data from code
function getSubdivisionFromCode(subdivisionCode: string) {
  if (!subdivisionCode) return null
  
  const subdivision = SUBDIVISIONS.find(s => s.code === subdivisionCode)
  return subdivision || null
}

// Utility function to convert subdivision code to province code
function convertSubdivisionToProvinceCode(subdivisionCode: string): string {
  const subdivision = getSubdivisionFromCode(subdivisionCode)
  return subdivision?.provinceCode || 'ON' // Default fallback to Ontario
}

export default function HomePage() {
  const [savedResult, setSavedResult] = useState<any>(null)
  const [initialData, setInitialData] = useState<any>(null)
  const [subdivisionData, setSubdivisionData] = useState<any>(null)
  const { isAuthenticated } = useAuth();
  const codeValues = useCodeValues();

  useEffect(() => {
    // Read URL parameters passed from the Angular app (token is received via postMessage)
    const urlParams = new URLSearchParams(window.location.search)

    try {
      // Map Angular form data to calculator form structure
      const angularData = {
        salary: urlParams.get('salary') || '',
        employerCostPerHour: urlParams.get('employerCostPerHour') || '',
        taxProvince: urlParams.get('taxProvince') || '',
        startDate: urlParams.get('startDate') || '',
        wcbCode: urlParams.get('wcbCode') || '',
        organizationId: urlParams.get('organizationId') || '',
        // CPP, QPP, and EI flags
        applyCpp: urlParams.get('applyCpp') || '',
        applyQpp: urlParams.get('applyQpp') || '',
        applyEi: urlParams.get('applyEi') || '',
        // Vacation/PTO data
        eligibleVacationDays: urlParams.get('eligibleVacationDays') || '',
        vacationDaysAccrued: urlParams.get('vacationDaysAccrued') || '',
        vacationDaysRemaining: urlParams.get('vacationDaysRemaining') || '',
        vacationDaysTaken: urlParams.get('vacationDaysTaken') || '',
        // Sick days data
        eligibleSickDays: urlParams.get('eligibleSickDays') || '',
        sickDaysRemaining: urlParams.get('sickDaysRemaining') || '',
        sickDaysTaken: urlParams.get('sickDaysTaken') || '',
        // Holiday data
        numPublicHolidays: urlParams.get('numPublicHolidays') || '',
        // Hours per day
        hoursPerDay: urlParams.get('hoursPerDay') || '',
        // Bonus fields
        bonusAmount: urlParams.get('bonusAmount') || '',
        bonusPercent: urlParams.get('bonusPercent') || '',
        // Source identifier for postMessage routing
        source: urlParams.get('source') || '',
      }

      // Get subdivision data - let the calculator handle the conversion
      const subdivision = getSubdivisionFromCode(angularData.taxProvince)
      
      // Store subdivision data for future WCB lookups
      setSubdivisionData(subdivision)
      
      console.log('Subdivision data:', {
        original: angularData.taxProvince,
        subdivision: subdivision,
        subdivisionId: subdivision?.id
      })

      // Convert to Labour Burden Calculator format
      const parsedBonusAmount = angularData.bonusAmount != null && angularData.bonusAmount !== ''
        ? Number(angularData.bonusAmount) : undefined
      const parsedBonusPercent = angularData.bonusPercent != null && angularData.bonusPercent !== ''
        ? Number(angularData.bonusPercent) : undefined
      
      const calculatorInitialData = {
        salary: angularData.salary ? Number(angularData.salary) : undefined,
        province: angularData.taxProvince ? convertSubdivisionToProvinceCode(angularData.taxProvince) : 'ON',
        vacationDays: angularData.eligibleVacationDays ? Number(angularData.eligibleVacationDays) : undefined,
        sickDays: angularData.eligibleSickDays ? Number(angularData.eligibleSickDays) : undefined,
        numPublicHolidays: angularData.numPublicHolidays ? Number(angularData.numPublicHolidays) : undefined,
        // Map WCB code if provided
        wsibCode: angularData.wcbCode || undefined,
        // Map organization ID for WCB rate lookup
        organizationId: angularData.organizationId ? Number(angularData.organizationId) : undefined,
        // Include subdivision ID for future WCB lookups
        subdivisionId: subdivision?.id,
        // Map CPP, QPP, and EI flags
        applyCpp: angularData.applyCpp ? angularData.applyCpp === 'true' : undefined,
        applyQpp: angularData.applyQpp ? angularData.applyQpp === 'true' : undefined,
        applyEi: angularData.applyEi ? angularData.applyEi === 'true' : undefined,
        // Map hours per day
        hoursPerDay: angularData.hoursPerDay ? Number(angularData.hoursPerDay) : undefined,
        // Map bonus fields
        bonusAmount: parsedBonusAmount != null && Number.isFinite(parsedBonusAmount) ? parsedBonusAmount : undefined,
        bonusPercent: parsedBonusPercent != null && Number.isFinite(parsedBonusPercent) ? parsedBonusPercent : undefined,
        // Calculate other fields from available data
        startDate: angularData.startDate || undefined,
        // Source identifier for postMessage routing (to distinguish modal vs profile calculator)
        source: angularData.source || undefined,
      }

      // Only set initial data if we have some parameters from Angular
      if (Object.values(angularData).some(value => value !== '')) {
        setInitialData(calculatorInitialData)
        console.log('Received data from Angular app:', angularData)
        console.log('Mapped to calculator format:', calculatorInitialData)
        
        // Log specific holiday data
        if (angularData.numPublicHolidays) {
          console.log('Public holidays received:', angularData.numPublicHolidays, 'Type:', typeof angularData.numPublicHolidays)
          console.log('Mapped to calculator publicHolidays:', calculatorInitialData.numPublicHolidays)
        } else {
          console.log('No public holidays data received from Angular')
        }
      }
    } catch (error) {
      console.error('Error processing URL parameters:', error)
    }
  }, [])

  const handleSaveResult = (data: any) => {
    setSavedResult(data)
    console.log("Saved labour burden calculation:", data)
  }

  function formatCurrency(value: number | string) {
    const numValue = typeof value === "string" ? Number.parseFloat(value) : value
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numValue)
  }

  // Combined loading spinner for authentication and code values
  if (!isAuthenticated || !codeValues.isReady) return (
    <div className="flex flex-col items-center justify-center min-h-[200px]">
      <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500 mb-4"></div>
      <span className="text-lg text-muted-foreground">
        {!isAuthenticated ? 'Loading authentication...' : 'Loading...'}
      </span>
    </div>
  );

  return (
    <div className="container mx-auto py-10 space-y-6">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold">Labour Burden Calculator</h1>
        <p className="text-muted-foreground mt-2">Calculate the true cost of employment based on salary, benefits, and province-specific rules</p>
      </div>

      <LabourBurdenCalculator 
        onSave={handleSaveResult}
        initialData={initialData}
      />

      {savedResult && (
        <Card>
          <CardHeader>
            <CardTitle>Saved Labour Burden Calculation</CardTitle>
            <CardDescription>Calculation saved on {new Date().toLocaleString()}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm font-medium">Annual Salary:</p>
                <p>{savedResult.salary ? formatCurrency(savedResult.salary) : "N/A"}</p>
              </div>
              <div>
                <p className="text-sm font-medium">Province:</p>
                <p>{savedResult.province}</p>
              </div>
              <div>
                <p className="text-sm font-medium">Vacation Days:</p>
                <p>{savedResult.vacationDays || "N/A"}</p>
              </div>
              <div>
                <p className="text-sm font-medium">Sick Days:</p>
                <p>{savedResult.sickDays || "N/A"}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

